import { Injectable } from '@angular/core';

import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';


import { HttpService } from '../http/http.service';
import { StorageKeysEnum } from '../enums/storage.keys.enum';
import { Company } from '../company/company.model';
import { CompanyUser } from '../user/companyUser.model';
import { NextMethodsEnum } from '../enums/next-methods.enum';
import { getTime } from 'date-fns';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class FileService {

    private cache: {[path: string]: [{width: number; height: number; skin: string; file: string}]} = {};

    constructor(
        private http: HttpService,
    ) { }

    upload(name: string, base64: string, extraPath: string = null): Observable<any> {
        const date = getTime(new Date());
        let foldersPath: string;
        if (extraPath) {
            foldersPath = `${environment.drive}/${environment.filePath}/${extraPath}`;
        } else {
            foldersPath = `${environment.drive}/${environment.filePath}`;
        }
        const path = `${foldersPath}/${date}_${name.toLowerCase().replace(/ /g, '_')}`;
        const company = JSON.parse(localStorage.getItem(StorageKeysEnum.company)) as Company;
        const user = JSON.parse(localStorage.getItem(StorageKeysEnum.user)) as CompanyUser;

        return this.http.post(NextMethodsEnum.uploadFile, {
            companyId: company.companyId,
            id: user.userId,
            uri: 'http://xml.cloudcodice.com/files/',
            name: path,
            base64,
        }).pipe(
            map (res => res.filename)
        );
    }

    remove(dropzone: any[]): Observable<boolean> {
        return this.http.post(NextMethodsEnum.removeDropzoneFile, {
            file: dropzone,
        }).pipe(
            map(result => result && result.roger),
        );
    }

    async download(path: string, dimensions?: {width?: number; height?: number}, skin: string = 'white-bg') {
        const cached = this.cache[path];
        if (cached) {
            const available = cached.filter((r) => {
                if (r.skin !== skin) {
                    return false;
                }
                switch (skin) {
                    case 'transparent-bg':
                        return r.width >= dimensions?.width && r.height >= dimensions?.height;
                    default:
                        return r.width === dimensions?.width && r.height === dimensions?.height;
                }
            });
            if (available.length > 0) {
                return available[0].file;
            }
        }
        const params: { labor?: string; fileName?: string; width?: number; height?: number; skin?: string; asJson?: boolean } = {
            labor: 'downloadFile',
            fileName: path,
            skin,
            asJson: true
        };
        if (dimensions?.height) {
            params.height = dimensions.height;
        }
        if (dimensions?.width) {
            params.width = dimensions.width;
        }

        const file: string = await firstValueFrom(this.http.get('formdata', { params }).pipe(
            map(response => response && response.json ? response.json : undefined)
        ));
        const entry = {width: dimensions?.width, height: dimensions?.height, skin, file};
        cached ? cached.push(entry) : this.cache[path] = [entry];
        return file;
    }

    getDownloadImageUrl(filename: string, height?: number, width?: number, skin = 'white-bg') {
        return `${this.http.getBaseUrl()}/formdata?labor=downloadFile&fileName=${filename}${height ? '&height=' + height : ''}${width ? '&width=' + width :
             ''}&skin=${skin}&asJson=false`;
    }

    getFileType(base64: string) { // returns mimetype (image/png)
        const data = base64.split(',')[0];
        if (data.split(/:|;/).length > 1) {
            const type = data.split(/:|;/)[1];
            return type.split('/');
        }
        return undefined;
    }

    getFileNameFromCOSPath(path: string) {
        // value wanted: 'file_name.example'
        return path // 'aaa/bbb/1234_file_name.example.bin'
            .split('/')       // ['aaa', 'bbb', '1234_file_name.example.bin']
            .slice(-1)[0]     // '1234_file_name.example.bin'
            .split('_')       // ['1234', 'file', 'name.example.bin']
            .slice(1)         // ['file', 'name.example.bin']
            .join('_')        // 'file_name.example.bin'
            .split('.')       // ['file_name', 'example', 'bin']
            .slice(0, -1)     // ['file_name', 'example']
            .join('.');       // 'file_name.example'
    }
}
